import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiClient from '../../api';
import { Button } from '../../components/common/Button';
import SupportWidget from '../../components/common/SupportWidget';
import LoadingScreen from '../../components/common/LoadingScreen';
import { 
  FaUserCircle, FaExternalLinkAlt, FaInstagram, FaFacebook, 
  FaPlus, FaBuilding, FaMapMarkerAlt, FaExclamationTriangle, 
  FaIdCard, FaPen, FaSave, FaTimes, FaSync, FaChartPie, FaCrown
} from 'react-icons/fa';

const META_APP_ID = process.env.REACT_APP_META_APP_ID;
const REDIRECT_URI = process.env.REACT_APP_REDIRECT_URI;

// --- Styled Components (MANTIDOS IGUAIS) ---
const ProfilePageContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  padding: 2rem 3rem;
  min-height: 100vh;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2.5rem;
`;

const PageTitle = styled.h2`
  font-size: 2.2rem;
  color: ${({ theme }) => theme.colors.secondary};
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: flex-start;

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.white};
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.07);
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${({ theme }) => theme.colors.background};
  padding-bottom: 0.75rem;
`;

const CardTitle = styled.h3`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.colors.secondary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  svg {
    font-size: 1.2rem;
    color: ${({ theme }) => theme.colors.light};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  min-height: 3rem;

  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: bold;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 0.95rem;
  flex: 1;
`;

const InfoValue = styled.span`
  color: ${({ theme }) => theme.colors.light};
  text-align: right;
  font-size: 0.95rem;
  max-width: 60%;
  word-wrap: break-word;
`;

const EditInput = styled.input`
  padding: 0.4rem 0.8rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 0.9rem;
  color: #333;
  text-align: right;
  width: 60%;
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const SectionSubtitle = styled.h4`
    margin-top: 1.5rem;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.primary};
    font-size: 1.1rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    font-weight: 700;
`;

const AddressEditGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-top: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;

  label {
    font-size: 0.8rem;
    font-weight: bold;
    color: #666;
  }
  
  input {
    padding: 0.5rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    width: 100%;
  }
`;

const PageList = styled.ul`
  list-style: none;
  padding: 0;
`;

const PageItem = styled.li`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f9f9f9;
  }

  svg {
    font-size: 1.5rem;
    color: ${props => props.platform === 'instagram' ? '#c32aa3' : '#4267B2'};
  }
`;

const PageName = styled.span`
  font-size: 1.1rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.secondary};
`;

const EmptyState = styled.div`
    text-align: center;
    padding: 2rem;
    color: ${({ theme }) => theme.colors.light};
`;

const ReconnectButton = styled(Button)`
  background-color: #ffc107;
  color: #333;
  border: none;
  font-size: 0.8rem;
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: #e0a800;
    transform: translateY(-2px);
  }
`;

const StatusBadge = styled.span`
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background-color: ${props => props.expired ? '#ffeeba' : '#d4edda'};
  color: ${props => props.expired ? '#856404' : '#155724'};
  margin-left: auto;
`;

const FeedbackMessage = styled.div`
    margin-top: 1rem;
    padding: 0.8rem;
    border-radius: 6px;
    background-color: ${props => props.type === 'error' ? '#fdecea' : '#eafaf1'};
    color: ${props => props.type === 'error' ? '#e74c3c' : '#2ecc71'};
    text-align: center;
    font-size: 0.9rem;
`;

const UsageItem = styled.div`margin-bottom: 1.2rem;`;
const UsageHeader = styled.div`display: flex; justify-content: space-between; margin-bottom: 0.5rem; font-size: 0.9rem; color: #555;`;
const ProgressBarContainer = styled.div`background: #eee; height: 8px; border-radius: 4px; overflow: hidden;`;
const ProgressBarFill = styled.div`
  background: ${props => props.overLimit ? '#e74c3c' : '#2ecc71'}; /* Verde se ok, Vermelho se estourou */
  width: ${props => Math.min(props.percentage, 100)}%;
  height: 100%;
  transition: width 0.5s ease;
`;
const PlanBadge = styled.div`
  background-color: #e3f2fd; color: #0d47a1; 
  padding: 0.5rem 1rem; border-radius: 20px; 
  font-weight: bold; font-size: 0.9rem; display: inline-flex; align-items: center; gap: 0.5rem;
`;

function PerfilCliente({ user, perfil }) {
  const [pages, setPages] = useState([]);
  const [isLoadingPages, setIsLoadingPages] = useState(true);
  
  // Estado para Edição
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', msg: '' });

  const navigate = useNavigate();

  // 1. Função isolada para buscar páginas (usada no load inicial e no refresh)
  const fetchPages = useCallback(async () => {
    if (!user?.cliente_id) return;
    setIsLoadingPages(true);
    try {
      const response = await apiClient.get(`/api/pages/${user.cliente_id}`);
      setPages(response.data);
    } catch (error) {
      console.error("Erro ao buscar as páginas do cliente:", error);
    } finally {
      setIsLoadingPages(false);
    }
  }, [user?.cliente_id]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  useEffect(() => {
    if (perfil) {
      setFormData({
        telefone: perfil.telefone || '',
        email_cobranca: perfil.email_cobranca || '',
        inscricao_estadual: perfil.inscricao_estadual || '',
        inscricao_municipal: perfil.inscricao_municipal || '',
        endereco_cep: perfil.endereco_cep || '',
        endereco_logradouro: perfil.endereco_logradouro || '',
        endereco_numero: perfil.endereco_numero || '',
        endereco_complemento: perfil.endereco_complemento || '',
        endereco_bairro: perfil.endereco_bairro || '',
        endereco_cidade: perfil.endereco_cidade || '',
        endereco_estado: perfil.endereco_estado || '',
      });
    }
  }, [perfil]);

  // --- NOVA LÓGICA DE POPUP ---
  const handleConnect = () => {
    const scope = 'instagram_business_basic,instagram_business_manage_insights';
    const authUrl = `https://www.instagram.com/oauth/authorize?force_reauth=true&client_id=${META_APP_ID}&redirect_uri=${REDIRECT_URI}&scope=${scope}&response_type=code`;
    
    // Configurações para centralizar o popup
    const width = 600;
    const height = 700;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    const popup = window.open(
        authUrl, 
        'Conectar Instagram', 
        `width=${width},height=${height},top=${top},left=${left},scrollbars=yes,status=yes`
    );

    // Monitora quando a janela ganha foco novamente (usuário fechou o popup ou clicou de volta)
    const handleFocus = () => {
        // Pequeno delay para garantir que o backend processou o callback caso tenha sido fechado
        setTimeout(() => {
            fetchPages();
        }, 1000);
        
        // Remove o listener para não ficar rodando sempre
        window.removeEventListener('focus', handleFocus);
    };

    window.addEventListener('focus', handleFocus);
  };

  const checkIsExpired = (expiresAtString) => {
    if (!expiresAtString) return false;
    const expiresDate = new Date(expiresAtString);
    const now = new Date();
    return expiresDate < now;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleEdit = () => {
    if (isEditing) {
      // Cancelar: reseta
      setFormData({
        telefone: perfil.telefone || '',
        email_cobranca: perfil.email_cobranca || '',
        inscricao_estadual: perfil.inscricao_estadual || '',
        inscricao_municipal: perfil.inscricao_municipal || '',
        endereco_cep: perfil.endereco_cep || '',
        endereco_logradouro: perfil.endereco_logradouro || '',
        endereco_numero: perfil.endereco_numero || '',
        endereco_complemento: perfil.endereco_complemento || '',
        endereco_bairro: perfil.endereco_bairro || '',
        endereco_cidade: perfil.endereco_cidade || '',
        endereco_estado: perfil.endereco_estado || '',
      });
      setFeedback({ type: '', msg: '' });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    setIsSaving(true);
    setFeedback({ type: '', msg: '' });

    try {
      await apiClient.put(`/api/cliente/${user.cliente_id}`, formData);
      setFeedback({ type: 'success', msg: 'Dados atualizados com sucesso!' });
      
      setTimeout(() => {
        setIsEditing(false);
        window.location.reload(); 
      }, 1500);

    } catch (error) {
      console.error("Erro ao salvar perfil:", error);
      setFeedback({ type: 'error', msg: 'Erro ao atualizar. Tente novamente.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (!perfil) return <LoadingScreen message="Carregando Perfil..." />;

  const isPJ = perfil.tipo_pessoa === 'PJ';
  
  const enderecoCompleto = [
    perfil.endereco_logradouro, 
    perfil.endereco_numero, 
    perfil.endereco_complemento, 
    perfil.endereco_bairro,
    perfil.endereco_cidade ? `${perfil.endereco_cidade}/${perfil.endereco_estado}` : null,
    perfil.endereco_cep
  ].filter(Boolean).join(', ');

  const calcPercent = (used, total) => {
    if (!total || total === 0) return 100; // Evita divisão por zero
    if (total > 99999) return (used > 0 ? 5 : 0); // Enterprise ilimitado mostra barra pequena se tiver uso
    return (used / total) * 100;
  };

  const proximoCiclo = new Date();
    proximoCiclo.setMonth(proximoCiclo.getMonth() + 1, 1);

  return (
    <ProfilePageContainer>
      <Header>
        <PageTitle>
          <FaUserCircle /> Meu Perfil
        </PageTitle>
        <Button onClick={() => navigate('/home')}>Voltar para o Dashboard</Button>
      </Header>

      <ProfileGrid>
        
        {/* COLUNA ESQUERDA (DADOS) */}
        <Column>
          {/* ... (Todo o código dos cards de dados da resposta anterior permanece idêntico aqui) ... */}
          {/* Para economizar espaço, mantenha o código do Card "Dados de Acesso" e "Dados do Cliente/Empresa" aqui */}
           <Card>
            <CardHeader>
                <CardTitle><FaUserCircle /> Dados de Acesso</CardTitle>
            </CardHeader>
            <InfoRow>
              <InfoLabel>Nome do Usuário</InfoLabel>
              <InfoValue>{perfil.nome}</InfoValue>
            </InfoRow>
            <InfoRow>
              <InfoLabel>E-mail de Login</InfoLabel>
              <InfoValue>{perfil.email}</InfoValue>
            </InfoRow>
          </Card>

          <Card>
            <CardHeader>
                <CardTitle>
                    {isPJ ? <FaBuilding /> : <FaIdCard />} 
                    {isPJ ? " Dados da Empresa" : " Dados do Cliente"}
                </CardTitle>
                <ActionButtons>
                  {isEditing ? (
                    <>
                      <Button variant="secondary" onClick={toggleEdit} disabled={isSaving} style={{padding:'5px 10px', fontSize:'0.8rem'}}>
                        <FaTimes /> Cancelar
                      </Button>
                      <Button onClick={handleSave} disabled={isSaving} style={{padding:'5px 10px', fontSize:'0.8rem'}}>
                         {isSaving ? 'Salvando...' : <><FaSave /> Salvar</>}
                      </Button>
                    </>
                  ) : (
                    <Button variant="outline" onClick={toggleEdit} style={{padding:'5px 10px', fontSize:'0.8rem'}}>
                      <FaPen /> Editar
                    </Button>
                  )}
                </ActionButtons>
            </CardHeader>

            {isPJ ? (
                <>
                    <InfoRow>
                        <InfoLabel>Razão Social</InfoLabel>
                        <InfoValue>{perfil.nome_empresa || '-'}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>CNPJ</InfoLabel>
                        <InfoValue>{perfil.cnpj || '-'}</InfoValue>
                    </InfoRow>
                </>
            ) : (
                <InfoRow>
                    <InfoLabel>CPF</InfoLabel>
                    <InfoValue>{perfil.cpf || '-'}</InfoValue>
                </InfoRow>
            )}

            <InfoRow>
              <InfoLabel>Telefone</InfoLabel>
              {isEditing ? (
                <EditInput name="telefone" value={formData.telefone} onChange={handleInputChange} placeholder="(00) 00000-0000" />
              ) : (
                <InfoValue>{perfil.telefone || '-'}</InfoValue>
              )}
            </InfoRow>

            <InfoRow>
              <InfoLabel>Email Financeiro</InfoLabel>
              {isEditing ? (
                <EditInput name="email_cobranca" value={formData.email_cobranca} onChange={handleInputChange} type="email" />
              ) : (
                <InfoValue>{perfil.email_cobranca || '-'}</InfoValue>
              )}
            </InfoRow>

            {isPJ && (
                <>
                    <SectionSubtitle>Dados Fiscais</SectionSubtitle>
                    <InfoRow>
                    <InfoLabel>Inscrição Estadual</InfoLabel>
                    {isEditing ? (
                        <EditInput name="inscricao_estadual" value={formData.inscricao_estadual} onChange={handleInputChange} />
                    ) : (
                        <InfoValue>{perfil.inscricao_estadual || 'Isento'}</InfoValue>
                    )}
                    </InfoRow>
                    <InfoRow>
                    <InfoLabel>Inscrição Municipal</InfoLabel>
                    {isEditing ? (
                        <EditInput name="inscricao_municipal" value={formData.inscricao_municipal} onChange={handleInputChange} />
                    ) : (
                        <InfoValue>{perfil.inscricao_municipal || 'Isento'}</InfoValue>
                    )}
                    </InfoRow>
                </>
            )}

            <SectionSubtitle><FaMapMarkerAlt style={{fontSize: '0.9rem'}}/> Endereço</SectionSubtitle>
            
            {isEditing ? (
              <AddressEditGrid>
                 <FormGroup>
                    <label>CEP</label>
                    <input name="endereco_cep" value={formData.endereco_cep} onChange={handleInputChange} />
                 </FormGroup>
                 <FormGroup>
                    <label>Estado (UF)</label>
                    <input name="endereco_estado" value={formData.endereco_estado} onChange={handleInputChange} maxLength="2" />
                 </FormGroup>
                 <FormGroup style={{gridColumn: '1 / -1'}}>
                    <label>Logradouro</label>
                    <input name="endereco_logradouro" value={formData.endereco_logradouro} onChange={handleInputChange} />
                 </FormGroup>
                 <FormGroup>
                    <label>Número</label>
                    <input name="endereco_numero" value={formData.endereco_numero} onChange={handleInputChange} />
                 </FormGroup>
                 <FormGroup>
                    <label>Bairro</label>
                    <input name="endereco_bairro" value={formData.endereco_bairro} onChange={handleInputChange} />
                 </FormGroup>
                 <FormGroup>
                    <label>Cidade</label>
                    <input name="endereco_cidade" value={formData.endereco_cidade} onChange={handleInputChange} />
                 </FormGroup>
                 <FormGroup>
                    <label>Complemento</label>
                    <input name="endereco_complemento" value={formData.endereco_complemento} onChange={handleInputChange} />
                 </FormGroup>
              </AddressEditGrid>
            ) : (
              <div style={{ marginTop: '0.5rem', color: '#666', lineHeight: '1.5' }}>
                  {enderecoCompleto || "Endereço não cadastrado"}
              </div>
            )}

            {feedback.msg && (
                <FeedbackMessage type={feedback.type}>{feedback.msg}</FeedbackMessage>
            )}

          </Card>
        </Column>

        {/* COLUNA DIREITA - PÁGINAS */}
        <Column>
            <Card>
                <CardHeader>
                    <CardTitle><FaChartPie /> Consumo do Plano</CardTitle>
                    {/* Exibe o nome do plano se vier do backend ou um ícone genérico */}
                    <PlanBadge><FaCrown /> {perfil.plan_code || 'Ativo'}</PlanBadge> 
                </CardHeader>

                {/* 1. Uso de Perfis */}
                <UsageItem>
                    <UsageHeader>
                        <span>Perfis Conectados</span>
                        <span>
                            {perfil.usage_profiles} / {perfil.plan_profiles_limit > 999 ? 'Ilimitado' : perfil.plan_profiles_limit}
                        </span>
                    </UsageHeader>
                    <ProgressBarContainer>
                        <ProgressBarFill 
                            percentage={calcPercent(perfil.usage_profiles, perfil.plan_profiles_limit)} 
                            overLimit={perfil.usage_profiles >= perfil.plan_profiles_limit}
                        />
                    </ProgressBarContainer>
                </UsageItem>

                {/* 2. Uso de Palavras-Chave */}
                <UsageItem>
                    <UsageHeader>
                        <span>Keywords Monitoradas</span>
                        <span>
                            {perfil.usage_keywords} / {perfil.plan_keywords_limit > 999 ? 'Ilimitado' : perfil.plan_keywords_limit}
                        </span>
                    </UsageHeader>
                    <ProgressBarContainer>
                        <ProgressBarFill 
                            percentage={calcPercent(perfil.usage_keywords, perfil.plan_keywords_limit)} 
                            overLimit={perfil.usage_keywords >= perfil.plan_keywords_limit}
                        />
                    </ProgressBarContainer>
                </UsageItem>

                {/* 3. Uso de Comentários (Soft Limit) */}
                <UsageItem>
                    <UsageHeader>
                        <span>Comentários Analisados (Mês)</span>
                        <span>
                           {/* Formata número ex: 1.200 */}
                           {perfil.usage_comments?.toLocaleString()} / {perfil.plan_comments_limit > 99999 ? 'Ilimitado' : perfil.plan_comments_limit?.toLocaleString()}
                        </span>
                    </UsageHeader>
                    <ProgressBarContainer>
                        <ProgressBarFill 
                            percentage={calcPercent(perfil.usage_comments, perfil.plan_comments_limit)} 
                            overLimit={perfil.usage_comments > perfil.plan_comments_limit}
                        />
                    </ProgressBarContainer>
                    {perfil.usage_comments > perfil.plan_comments_limit && (
                        <small style={{color: '#e67e22', display: 'block', marginTop: '0.25rem'}}>
                            <FaExclamationTriangle /> Você excedeu o volume mensal. Entre em contato para upgrade.
                        </small>
                    )}
                </UsageItem>

                <div style={{marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #eee', fontSize: '0.85rem', color: '#888'}}>
                    Ciclo atual: renova em {proximoCiclo.toLocaleDateString('pt-BR', {day: 'numeric', month: 'long'})}
                </div>
            </Card>
            <Card>
            <CardHeader>
                <CardTitle>Páginas Conectadas</CardTitle>
                <div style={{display: 'flex', gap: '8px'}}>
                    {/* Botão de atualizar manual caso o automático falhe */}
                    <Button variant="outline" onClick={fetchPages} style={{padding: '8px 12px'}} title="Atualizar Lista">
                        <FaSync />
                    </Button>
                    <Button variant="secondary" onClick={handleConnect} style={{padding: '8px 16px', fontSize: '0.9rem'}}>
                        <FaPlus /> Nova
                    </Button>
                </div>
            </CardHeader>
            
            {isLoadingPages ? (
                <p>Carregando páginas...</p>
            ) : pages.length > 0 ? (
              <PageList>
                {pages.map(page => {
                  const isExpired = checkIsExpired(page.expires_at);

                  return (
                    <PageItem key={page.page_id} platform={page.platform}>
                      {page.platform === 'instagram' ? <FaInstagram /> : <FaFacebook />}
                      
                      <div style={{display: 'flex', flexDirection: 'column'}}>
                        <PageName>{page.name}</PageName>
                      </div>

                      {isExpired ? (
                        <div style={{marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '10px'}}>
                            <StatusBadge expired>Expirado</StatusBadge>
                            <ReconnectButton onClick={handleConnect} title="Sessão expirada. Clique para reconectar.">
                                <FaExclamationTriangle /> Reconectar
                            </ReconnectButton>
                        </div>
                      ) : (
                        <StatusBadge>Ativo</StatusBadge>
                      )}
                    </PageItem>
                  );
                })}
              </PageList>
            ) : (
                <EmptyState>
                <p>Nenhuma página social foi conectada ainda.</p>
                <Button 
                    style={{marginTop: '1rem'}}
                    onClick={handleConnect}
                >
                    <FaExternalLinkAlt /> Conectar Primeira Página
                </Button>
                </EmptyState>
            )}
            </Card>
        </Column>

      </ProfileGrid>
      <SupportWidget user={user} />
    </ProfilePageContainer>
  );
}

export default PerfilCliente;